import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import DatePicker from 'react-datepicker'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../fragments'
import * as Sentry from '@sentry/browser'

import {
  Heading,
  TextArea,
  Button,
  CaseChooseAssignedTo,
  CaseChoosePriority,
  Picklist,
  PicklistWithData,
  CheckboxToggle,
  EmployerPicklist
} from '../'

import { CASE_TYPE_MAP_EDIT } from '../../constants'

const getStateDefault = (props) => {
  let nextActionAt = props.nextActionAt
    ? moment(props.nextActionAt)
    : null

  if (nextActionAt && nextActionAt.isSameOrBefore(moment(), 'day')) {
    nextActionAt = moment().add(1, 'days').hour(16).minute(0)
  }

  return {
    affects: props.affects,
    productType: props.productType ? props.productType.toLowerCase() : null,
    requestType: props.requestType,
    assignedTo: props.assignedTo,
    nextActionAt: props.assign && nextActionAt ? moment(nextActionAt) : moment().add(1, 'days').hour(16).minute(0),
    priority: props.assign && props.priority ? props.priority : 'B',
    caseNote: props.assign && props.caseNote ? props.caseNote : '',
    followUp: props.followUp || props.assign
  }
}


class CaseCreateAssign extends React.PureComponent {
  // This component can be used to create a case or edit existing case details with.
  // PROPS:
  // Case reassignment is a specific type of edit, indicated with the 'assign' prop set to true.
  // Editing as case require the passing in of a case's current detail values as props.
  // Specifying 'caseRelations' will ensure that case relations, specified as an array, will get saved againt the case.
  // Several optional function props can be specified to hook into execution outcomes of the case save process, that can consist of three steps:
  //  1. saving case details
  //  2. saving case notes
  //  3. save case relations

  static propTypes = {
    case: PropTypes.string,
    affects: PropTypes.string.isRequired,
    productType: PropTypes.string,
    requestType: PropTypes.string,
    assignedTo: PropTypes.string,
    nextActionAt: PropTypes.string,
    priority: PropTypes.string,
    followUp: PropTypes.bool,
    onCancelClick: PropTypes.func,
    onSave: PropTypes.func,
    onCaseSaveFail: PropTypes.func,
    onCaseNoteSaveSuccess: PropTypes.func,
    onCaseNoteSaveFail: PropTypes.func,
    onCaseRelationSaveSucces: PropTypes.func,
    onCaseRelationSaveFail: PropTypes.func,
    onCaseRelationsCreateSuccess: PropTypes.func,
    onCaseRelationsCreateFail: PropTypes.func,
    saveDisabled: PropTypes.bool,
    assign: PropTypes.bool,
    selectAffectsEmployer: PropTypes.bool,
    caseRelations: PropTypes.arrayOf(PropTypes.shape({
      objectId: PropTypes.string.isRequired,
      contentType: PropTypes.string.isRequired
    }))
  }

  static defaultProps = {
    assign: false,
    followUp: false,
    productType: '',
    requestType: ''
  }

  state = getStateDefault(this.props)

  onSave = (caseId) => {
    if (this.props.onSave) {
      this.props.onSave(caseId)
    }
  }

  postSave = (caseId) => {
    if (this.props.caseRelations) {
      // for every case relation, save
      this.saveCaseRelations(this.props.caseRelations, caseId)
    } else {
      this.onSave(caseId)
    }
  }

  saveCaseRelations = (caseRelations, caseId) => {
    let caseRelationSavePromises = []

    caseRelations.forEach(relation => {
      caseRelationSavePromises.push(this.createCaseRelation(caseId, relation.contentType, relation.objectId))
    })

    Promise.all(caseRelations)
    .then(values => this.onSave(caseId))
    .catch(error => Sentry.captureMessage(`error in saving case relations - ${error}`))
  }

  saveCase = () => {
    let currentUser = localStorage.getItem('userUUID')
    let input = {}

    if (this.props.assign) {  // case reassignment
      input.id = this.props.case
      input.assignedTo = this.state.assignedTo
      input.affects = this.state.affects
      if (this.state.status !== this.props.status) {
        input.status = this.state.status
      }
      if (this.state.priority !== this.props.priority) {
        input.priority = this.state.priority.toLowerCase()
      }
      if (!this.state.nextActionAt.isSame(moment(this.props.nextActionAt))) {
        input.nextActionAt = this.state.nextActionAt
          .utc()
          .format("YYYY-MM-DD HH:mm:ss.SSS");
      }
    } else {  // case create/edit
      input.affects = this.state.affects
      if (this.state.productType) {
        input.productType = this.state.productType
        input.requestType = this.state.requestType
      }

      if (this.props.case) {  // in case of case edit
        input.id = this.props.case
      } else {  // in case of case creation
        input.openedBy = currentUser
      }
      if (this.state.followUp) {
        input.assignedTo = this.state.assignedTo
        input.nextActionAt = this.state.nextActionAt
          .utc()
          .format("YYYY-MM-DD HH:mm:ss.SSS");
        input.priority = this.state.priority.toLowerCase()
        input.status = 'open'
      } else {
        input.assignedTo = currentUser
        input.status = 'resolved'
        input.priority = 'b'
        input.nextActionAt = moment().utc().format("YYYY-MM-DD HH:mm:ss.SSS");
      }
    }

    this.props.saveCase({ variables: { input }})
    .then(({ data }) => {
      console.log(`Case saved successfully`)
      if (this.state.caseNote.length > 0) {
        this.saveCaseNote(data.caseMutate.case.id, currentUser)
      } else {
        this.postSave(data.caseMutate.case.id)
      }
    })
    .catch((error) => {
      console.log(`Case save failed!`, error)
      if (this.props.onCaseSaveFail) {
        this.props.onCaseSaveFail()
      }
    })
  }

  saveCaseNote = (caseId, currentUser) => {
    let input = {
      case: caseId,
      note: this.state.caseNote,
      author: currentUser,
      direction: "internal"
    }

    this.props.saveCaseNote({ variables: { input } })
    .then(() => {
      console.log("Case note saved successfully")
      this.setState({ caseNote: '' })
      this.postSave(caseId)
    })
    .catch((error) => {
      console.log('Case note save failed!', error)
      this.setState({ caseNote: '' })
      this.onSave()
      if (this.props.onCaseNoteSaveFail) {
        this.props.onCaseNoteSaveFail()
      }
    })
  }

  createCaseRelation = (caseId, contentType, objectId) => {
    let input = {
      case: caseId,
      contentType,
      objectId
    }
    this.props.saveCaseRelation({ variables: { input }})
    .then(() => {
      console.log('Case relation saved successfully')
      this.onSave()
      if (this.props.onCaseRelationsCreateSuccess) {
        this.props.onCaseRelationsCreateSuccess()
      }
    })
    .catch((err) => {
      console.log('Failed to save case relation', err)
      if (this.props.onCaseRelationsCreateFail) {
        this.props.onCaseRelationsCreateFail()
      }
    })
  }

  render() {
    let {
      case: caseId,
      onCancelClick,
      assign,
      selectAffectsEmployer
    } = this.props

    return (
      <div className="case-assign-section slds-p-around_large">
        {!assign &&
          <React.Fragment>
            {selectAffectsEmployer &&
              <div className='slds-size_1-of-3 slds-m-bottom_large'>
                <Heading size="medium">
                  Affects employer
                </Heading>
                <EmployerPicklist
                  id='affectsEmployer'
                  value={this.state.affects}
                  symbol='down'
                  onChange={affects => this.setState({ affects })}
                  maxHeight={400}
                  closeDropdownOnSelect={true}
                />
              </div>
            }
            <div className='slds-size_1-of-3 slds-m-bottom_large'>
              <Heading size="medium">
                Product and request type
              </Heading>
              <PicklistWithData
                id="productType"
                label="Product"
                forField="CaseProductType"
                value={this.state.productType || ''}
                onChange={productType => this.setState({
                  productType,
                  requestType: ''
                })}
                closeDropdownOnSelect={true}
              />
              <Picklist
                id="requestType"
                options={this.state.productType
                  ? CASE_TYPE_MAP_EDIT[this.state.productType]
                  : CASE_TYPE_MAP_EDIT['']
                }
                value={this.state.requestType}
                onChange={requestType => this.setState({ requestType })}
                closeDropdownOnSelect={true}
              />
            </div>
            <CheckboxToggle
              id='followUpToggle'
              label='Follow up?'
              checked={this.state.followUp}
              onChange={() => this.setState(prevState => ({ followUp: !prevState.followUp }))}
              className='slds-m-bottom_medium u-font-size-larger'
            />
          </React.Fragment>
        }
        <div className='slds-grid slds-wrap'>
          {this.state.followUp &&
            <React.Fragment>
              <div className="case-assign-section_panel slds-p-around_medium">
                <Heading size="medium">
                  Assign to
                </Heading>
                <div className="slds-text-align_left">
                  <div className="slds-scrollable_y slds-p-left_x-small slds-p-right_x-small slds-p-bottom_large" style={{ height: 220 }}>
                    <CaseChooseAssignedTo
                      case={caseId}
                      assignedTo={this.state.assignedTo}
                      onChoice={identity => this.setState({ assignedTo: identity })}
                    />
                  </div>
                </div>
              </div>
              <div className="case-assign-section_panel slds-p-around_medium">
                <Heading size="medium">
                  Next action date
                </Heading>
                <div
                  className="datepicker-no_padding_right">
                  <DatePicker
                    selected={moment(this.state.nextActionAt)}
                    onChange={nextActionAt => this.setState({ nextActionAt })}
                    minDate={moment()}
                    openToDate={moment()}
                    showTimeSelect
                    timeFormat="HH:mm"
                    minTime={moment().hours(8).minutes(0)}  // working hours
                    maxTime={moment().hours(17).minutes(0)}
                    inline={true}
                  />
                </div>
              </div>
              <div className="case-assign-section_panel slds-p-around_medium">
                <Heading size="medium">
                  Priority
                </Heading>
                <CaseChoosePriority
                  case={caseId}
                  priority={this.state.priority}
                  onClose={() => this.setState({ selectPriority: false })}
                  onChoice={priority => this.setState({ priority })}
                />
              </div>
            </React.Fragment>
          }
        </div>
        <div className='slds-size_2-of-3 slds-m-top_large'>
          <Heading size="medium" marginBottom='5'>
            Add a case note
          </Heading>
          <TextArea
            onChange={e => this.setState({ caseNote: e.target.value })}
            className="fullwidth"
            rows={5}
          />
        </div>
        <div className='slds-grid slds-m-top_x-large'>
          <Button
            id='caseAssignCreateCancel'
            onClick={() => {
              if (onCancelClick) {
                onCancelClick()
              }
              this.setState(getStateDefault(this.props))
            }}
            minWidth={100}
            className='slds-size_1-of-2'>
            Cancel
          </Button>
          <Button
            id='caseAssignCreateConfirm'
            type='brand'
            onClick={this.saveCase}
            minWidth={100}
            className='slds-size_1-of-2 slds-m-left_medium'
            disabled={this.props.assign
              ? this.state.assignedTo === this.props.assignedTo &&
                this.state.priority === this.props.priority &&
                this.state.caseNote.length === 0  // remove condition if we don't want to enforce adding case note with reassignment
              : this.state.followUp
                ? this.state.requestType === null || this.state.requestType === '' ||
                  this.state.assignedTo === null || !this.state.affects
                : this.state.requestType === null || this.state.requestType === ''
            }>
            {assign ? 'ASSIGN' : 'CREATE'}
          </Button>
        </div>
      </div>
    )
  }
}

const saveCase = gql`
  mutation($input: CaseMutationInput!) {
    caseMutate(input: $input) {
      case {
        id
        priority
        nextActionAt
        status
        productType
        requestType
        assignedTo {
          id
          firstName
          lastName
        }
      }
    }
  }
`

const saveCaseNote = gql`
  mutation saveCaseNote($input: CaseNoteMutationInput!) {
    casenoteMutate(input: $input) {
      casenote {
        ...CaseNote
      }
    }
  }
  ${fragments.casenote}
`

const saveCaseRelation = gql`
  mutation saveCaseRelaton($input: CaseRelationMutationInput!) {
    caserelationMutate(input: $input) {
      caserelation {
        id
      }
    }
  }
`

const withCaseMutate =
  graphql(saveCase, {
    name: "saveCase"
  })

const withCaseNoteMutate =
  graphql(saveCaseNote, {
    name: "saveCaseNote"
  })

const withCaseRelationMutate =
  graphql(saveCaseRelation, {
    name: "saveCaseRelation"
  })

export default compose(
  withCaseMutate,
  withCaseNoteMutate,
  withCaseRelationMutate
)(CaseCreateAssign)



// WEBPACK FOOTER //
// ./src/components/case-create-assign/index.js